import { useState, useEffect } from 'react';
import { X, MapPin, Link as LinkIcon } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';

interface EventoModalProps {
  evento?: any;
  onClose: () => void;
  onSave: (data: any) => void;
  onDelete: (id: number) => void;
}

const COLORS = ['#3B82F6', '#EF4444', '#22C55E', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6'];

export function EventoModal({ evento, onClose, onSave, onDelete }: EventoModalProps) {
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    fechaInicio: '',
    fechaFin: '',
    todoElDia: false,
    color: '#3B82F6',
    tipo: 'evento',
    ubicacion: '',
    url: '',
    notificar: false,
    recordatorio: '',
  });

  useEffect(() => {
    if (evento) {
      setFormData({
        titulo: evento.titulo || '',
        descripcion: evento.descripcion || '',
        fechaInicio: evento.fechaInicio ? evento.fechaInicio.slice(0, 16) : '',
        fechaFin: evento.fechaFin ? evento.fechaFin.slice(0, 16) : '',
        todoElDia: evento.todoElDia || false,
        color: evento.color || '#3B82F6',
        tipo: evento.tipo || 'evento',
        ubicacion: evento.ubicacion || '',
        url: evento.url || '',
        notificar: evento.notificar || false,
        recordatorio: evento.recordatorio || '',
      });
    }
  }, [evento]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">
            {evento ? 'Editar Evento' : 'Nuevo Evento'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
            <Input
              value={formData.titulo}
              onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
              placeholder="Título del evento"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              placeholder="Descripción del evento"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Inicio</label>
              <Input
                type="datetime-local"
                value={formData.fechaInicio}
                onChange={(e) => setFormData({ ...formData, fechaInicio: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Fin</label>
              <Input
                type="datetime-local"
                value={formData.fechaFin}
                onChange={(e) => setFormData({ ...formData, fechaFin: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="todoElDia"
              checked={formData.todoElDia}
              onChange={(e) => setFormData({ ...formData, todoElDia: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="todoElDia" className="text-sm text-gray-700">Todo el día</label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormData({ ...formData, color })}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    formData.color === color ? 'border-gray-800 scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select
              value={formData.tipo}
              onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="evento">Evento</option>
              <option value="nota">Nota</option>
              <option value="recordatorio">Recordatorio</option>
              <option value="trayecto">Trayecto</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ubicación</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <Input
                value={formData.ubicacion}
                onChange={(e) => setFormData({ ...formData, ubicacion: e.target.value })}
                placeholder="Ubicación"
                className="pl-10"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
            <div className="relative">
              <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <Input
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://..."
                className="pl-10"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="notificar"
              checked={formData.notificar}
              onChange={(e) => setFormData({ ...formData, notificar: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="notificar" className="text-sm text-gray-700">Notificar recordatorio</label>
          </div>

          {formData.notificar && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Recordatorio</label>
              <Input
                type="datetime-local"
                value={formData.recordatorio}
                onChange={(e) => setFormData({ ...formData, recordatorio: e.target.value })}
              />
            </div>
          )}

          <div className="flex gap-3 pt-4">
            {evento && (
              <Button
                type="button"
                onClick={() => onDelete(evento.id)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Eliminar
              </Button>
            )}
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
              {evento ? 'Actualizar' : 'Crear'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}